/**
 * Extracts the navigator state string from a NavigatorState object.
 * The protobuf may or may not include the `state` enum field. When it's missing,
 * we infer it from the populated sub-field key (off, generating_path, ready, engaged, interrupted).
 */

// Decoded with protobufjs `keepCase: true`, so oneof fields arrive snake_case.
// The camelCase alias guards against that option changing.
const SUB_FIELD_TO_STATE: Record<string, string> = {
  off: "OFF",
  generating_path: "GENERATING_PATH",
  generatingPath: "GENERATING_PATH",
  ready: "READY",
  engaged: "ENGAGED",
  interrupted: "INTERRUPTED",
};

export function getNavigatorState(navigatorStateObj: any): string | undefined {
  if (!navigatorStateObj || typeof navigatorStateObj !== "object") return undefined;

  // Prefer the explicit state enum if present
  if (navigatorStateObj.state) return navigatorStateObj.state;

  // Infer from the populated sub-field key
  for (const [key, enumValue] of Object.entries(SUB_FIELD_TO_STATE)) {
    if (navigatorStateObj[key] !== undefined) return enumValue;
  }

  return undefined;
}

/**
 * Navigator/DebugString values meaning it cannot plan against the jobsite
 * geometry. VADC still reports ACTIVE in this state, so autonomy looks engaged
 * while the machine can never actually move.
 * Keep the operator-facing geometry error copy in sync with this list.
 */
const GEOMETRY_FAULT_DEBUG_STRINGS = ["no outer boundary"];

/** Returns the navigator's geometry fault text, or undefined when it can plan. */
export function getNavigatorGeometryFault(navigatorObj: any): string | undefined {
  const debugString = navigatorObj?.DebugString;
  if (typeof debugString !== "string") return undefined;
  const trimmed = debugString.trim();
  return GEOMETRY_FAULT_DEBUG_STRINGS.includes(trimmed.toLowerCase()) ? trimmed : undefined;
}

/**
 * Extracts the navigator state from an autonomy log entry.
 * VADC entries have { "0": "2", timestamp: ... }
 * Navigator entries have { interrupted: { transitionReason: "" }, timestamp: ... }
 */
export function getAutonomyLogNavigatorState(logEntry: any): string | undefined {
  if (!logEntry || typeof logEntry !== "object") return undefined;

  // Check explicit state field first
  if (logEntry.state) return logEntry.state;

  // Check for navigator sub-field keys
  for (const [key, enumValue] of Object.entries(SUB_FIELD_TO_STATE)) {
    if (logEntry[key] !== undefined) return enumValue;
  }

  return undefined;
}
